import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField, Modal, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import MenuSection from "../components/MenuSection";
import { handlePagoEvent, showLoadingSpinner, setTarjetaID } from "../helpers/pagosHelper";
import estadoDespacho from "../api/estadoDespacho";
import ConsultarSaldoShellBox from "../api/consultarSaldoShellBox";
import { useCanjeStore } from "../hooks";

// Iconos de métodos de pago
import DebitoIcon from "../images/debito.png";
import EfectivoPagoIcon from "../images/efectivo-icon.png";
import CreditoIcon from "../images/credito.png";
import qr from "../images/qr.png";

const DividirPagoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { premio, dniCanje } = useCanjeStore();

  // Estados principales
  const [montoTotal, setMontoTotal] = useState(0);
  const [montoRestante, setMontoRestante] = useState(0);
  const [pagosRealizados, setPagosRealizados] = useState([]);

  const [showQRModal, setShowQRModal] = useState(false);

  const [mostrarOverlayQR, setMostrarOverlayQR] = useState(false);

  // Estados del modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [metodoPagoSeleccionado, setMetodoPagoSeleccionado] = useState(null);
  const [montoIngresado, setMontoIngresado] = useState("");

  // Estados de carga
  const [isLoading, setIsLoading] = useState(false);
  const [procesandoPago, setProcesandoPago] = useState(false);

  // Estados de error
  const [error, setError] = useState("");

  // Estados para puntos Shell
  const [puntosShellInfo, setPuntosShellInfo] = useState(null);

  const [mensajeDeEspera, setMensajeDeEspera] = useState("");

  // Estado para modal de confirmación al volver
  const [modalConfirmacionVolver, setModalConfirmacionVolver] = useState(false);

  // Detectar parámetros de puntos Shell de la URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sugerirRedimir = searchParams.get("sugerirRedimirLatamPass");
    const acumulaPuntos = searchParams.get("acumulaPuntos");

    if (sugerirRedimir !== null || acumulaPuntos !== null) {
      setPuntosShellInfo({
        sugerirRedimir: sugerirRedimir === "true",
        acumulaPuntos: acumulaPuntos === "true",
      });
    }
  }, [location]);

  useEffect(() => {
    const facturaInfo = JSON.parse(localStorage.getItem("facturaInfo"));
    let totalProductos = facturaInfo.total;

    // Aplicar descuentos si existen
    if (dniCanje && premio?.descuento) {
      totalProductos -= premio.descuento;
    }

    setMontoTotal(totalProductos);
    setMontoRestante(totalProductos);

    // Esto evita mezclar cupones de sesiones anteriores con los nuevos
    localStorage.removeItem("pagosMultiples");

    // Recuperar solo pagos parciales guardados intencionalmente (no los múltiples)
    recuperarProgresoPagos(totalProductos);
  }, [dniCanje, premio]);

  // Función para recuperar progreso de pagos guardados
  const recuperarProgresoPagos = async totalProductos => {
    try {
      // Solo verificar pagos parciales (guardados intencionalmente por el usuario)
      const pagosParciales = localStorage.getItem("pagosParciales");

      if (pagosParciales) {
        let pagosARecuperar = JSON.parse(pagosParciales);
        console.log("🔄 Recuperando pagos parciales guardados:", pagosARecuperar);

        // Recalcular idTarjeta para pagos que no lo tengan
        const tarjetasData = JSON.parse(localStorage.getItem("tarjetas")) || [];

        for (let pago of pagosARecuperar) {
          if (pago.cuponClover && !pago.cuponClover.idTarjeta && !pago.idTarjeta) {
            try {
              const cuponString = JSON.stringify(pago.cuponClover);
              const idTarjeta = await setTarjetaID(cuponString, tarjetasData);
              console.log("🔄 Recalculando idTarjeta para pago recuperado:", idTarjeta);

              pago.cuponClover = {
                idTarjeta: idTarjeta,
                ...pago.cuponClover,
              };
              pago.idTarjeta = idTarjeta;
            } catch (error) {
              console.error("Error recalculando idTarjeta:", error);
            }
          }
        }

        setPagosRealizados(pagosARecuperar);

        // Calcular monto restante basado en pagos guardados
        const totalPagado = pagosARecuperar.reduce((sum, pago) => sum + pago.monto, 0);
        setMontoRestante(totalProductos - totalPagado);

        // Limpiar pagos guardados después de recuperarlos
        localStorage.removeItem("pagosParciales");
      }
    } catch (error) {
      console.error("Error recuperando progreso de pagos:", error);
    }
  };

  // Función para guardar progreso en localStorage
  const guardarProgresoPagos = () => {
    try {
      if (pagosRealizados.length > 0) {
        localStorage.setItem("pagosParciales", JSON.stringify(pagosRealizados));
      }
    } catch (error) {
      console.error("Error guardando progreso de pagos:", error);
    }
  };

  // Event listeners para pagos
  useEffect(() => {
    const handleEfectivoEvent = async event => {
      const responsePagoEfectivoJava = event.detail.success;

      if (responsePagoEfectivoJava) {
        await finalizarPagoParcial("efectivo", parseFloat(montoIngresado), null);
      } else {
        setError("Error en el pago con efectivo");
        setProcesandoPago(false);
      }
    };

    const handleTarjetaEvent = async event => {
      const response = event.detail.success;
      const jsonString = JSON.stringify(event.detail.responsePagoConTarjeta);

      if (response) {
        // NO guardar CuponClover individual para pagos múltiples
        // Solo parsear el cupón para incluirlo en el pago múltiple
        const cuponClover = JSON.parse(jsonString);
        await finalizarPagoParcial(metodoPagoSeleccionado, parseFloat(montoIngresado), cuponClover);
      } else {
        setError(`Error en el pago con ${metodoPagoSeleccionado}`);
        setProcesandoPago(false);
      }
    };

    window.addEventListener("responsePagoEfectivo", handleEfectivoEvent);
    window.addEventListener("responsePagoConTarjeta", handleTarjetaEvent);

    return () => {
      window.removeEventListener("responsePagoEfectivo", handleEfectivoEvent);
      window.removeEventListener("responsePagoConTarjeta", handleTarjetaEvent);
    };
  }, [metodoPagoSeleccionado, montoIngresado]);

  const abrirModalMonto = metodoPago => {
    setMetodoPagoSeleccionado(metodoPago);
    setMontoIngresado("");
    setError("");
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setMetodoPagoSeleccionado(null);
    setMontoIngresado("");
    setError("");
  };

  const validarMonto = () => {
    const monto = parseFloat(montoIngresado);

    if (isNaN(monto) || monto <= 0) {
      setError("Ingrese un monto válido");
      return false;
    }

    if (monto > montoRestante) {
      setError(`El monto no puede ser mayor a $${montoRestante.toLocaleString("es-AR", { minimumFractionDigits: 2 })}`);
      return false;
    }

    return true;
  };

  const handleQRFiserv = () => {
    setShowQRModal(true); // Muestra el modal con el QR
    setProcesandoPago(false); // Asegura que el overlay no esté visible aún
    setMostrarOverlayQR(false); // Tampoco mostrar overlay antes de tiempo

    // ⏱️ A los 6 segundos, cerramos el modal del QR y mostramos el overlay "PROCESANDO PAGO..."
    setTimeout(() => {
      setShowQRModal(false); // Cierra el QR
      setProcesandoPago(true); // Muestra el overlay "PROCESANDO PAGO..."
    }, 6000);

    // ⏱️ A los 8 segundos (2s después del cierre), finalizamos el pago
    setTimeout(() => {
      const cuponSimulado = {
        codigoAutorizacion: "QR123",
        ultimosCuatro: "0000",
        marca: "QR FISERV",
        nombre: "Pago QR",
      };
      finalizarPagoParcial("qr", parseFloat(montoIngresado), cuponSimulado);
    }, 8000);
  };

  const procesarPago = async () => {
    if (!validarMonto()) return;

    setError("");

    const monto = parseFloat(montoIngresado);

    try {
      switch (metodoPagoSeleccionado) {
        case "debito":
        case "credito":
          setProcesandoPago(true);
          await procesarPagoTarjeta(monto);
          break;
        case "efectivo":
          setProcesandoPago(true);
          await procesarPagoEfectivo(monto);
          break;
        case "qr":
          handleQRFiserv(); // NO SETEES `setProcesandoPago(true)` aquí
          return;
        default:
          throw new Error("Método de pago no válido");
      }
    } catch (error) {
      console.error("Error procesando pago:", error);
      setError("Error al procesar el pago");
      setProcesandoPago(false);
    }
  };

  const procesarPagoTarjeta = async monto => {
    setMensajeDeEspera("ESPERANDO TARJETA...");
    setProcesandoPago(false); // ocultamos "procesando" mientras esperamos tarjeta

    setTimeout(() => {
      setMensajeDeEspera(""); // ocultamos mensaje de espera
      setProcesandoPago(true); // ahora sí mostramos "PROCESANDO PAGO..."

      const cuponSimulado = {
        codigoAutorizacion: "123456",
        ultimosCuatro: "0000",
        marca: metodoPagoSeleccionado.toUpperCase(),
        nombre: "Simulado",
      };

      setTimeout(() => {
        finalizarPagoParcial(metodoPagoSeleccionado, monto, cuponSimulado);
      }, 2000);
    }, 6000); // 6s esperando tarjeta
  };

  const procesarPagoEfectivo = async monto => {
    setMensajeDeEspera("ESPERANDO PAGO EN EFECTIVO");
    setProcesandoPago(false); // Mostrar solo el mensaje

    // Simular tiempo de espera antes de procesar el pago
    setTimeout(() => {
      setMensajeDeEspera(""); // Ocultar el mensaje
      setProcesandoPago(true); // Mostrar spinner y mensaje de procesamiento final
    }, 3000);

    setTimeout(() => {
      finalizarPagoParcial("efectivo", monto, null);
    }, 5000);
  };

  const finalizarPagoParcial = async (metodoPago, monto, cuponClover) => {
    try {
      let idTarjeta = null;

      // Si hay cupón de tarjeta, calcular el idTarjeta
      if (cuponClover) {
        try {
          const tarjetasData = JSON.parse(localStorage.getItem("tarjetas")) || [];
          const cuponString = JSON.stringify(cuponClover);
          idTarjeta = await setTarjetaID(cuponString, tarjetasData);

          // Agregar idTarjeta al cupón
          cuponClover = {
            idTarjeta: idTarjeta,
            ...cuponClover,
          };
        } catch (error) {
          console.error("Error calculando idTarjeta:", error);
        }
      }

      // Registrar el pago realizado
      const nuevoPago = {
        id: Date.now(),
        metodoPago,
        monto,
        timestamp: new Date(),
        estado: "completado",
        cuponClover: cuponClover,
        idTarjeta: idTarjeta, // También guardarlo a nivel del pago
      };

      // Actualizar el estado de pagos realizados
      const nuevosPageosRealizados = [...pagosRealizados, nuevoPago];
      setPagosRealizados(nuevosPageosRealizados);
      setMontoRestante(prev => prev - monto);

      // GUARDA EN LOCALSTORAGE EL ARRAY DE PAGOS
      localStorage.setItem("pagosMultiples", JSON.stringify(nuevosPageosRealizados));

      // Cerrar modal y resetear estados
      cerrarModal();
      setProcesandoPago(false);

      // Si el monto restante es 0 o menor, el pago está completo
      if (montoRestante - monto <= 0) {
        // Aquí podrías ejecutar la lógica final de completar la venta
        console.log("Pago completado totalmente");
      }
    } catch (error) {
      console.error("Error finalizando pago parcial:", error);
      setError("Error al registrar el pago");
      setProcesandoPago(false);
    }
  };

  const finalizarVentaCompleta = async () => {
    setIsLoading(true);

    try {
      const despachosSeleccionados = localStorage.getItem("despachosSeleccionados");

      if (despachosSeleccionados) {
        const despachosArray = JSON.parse(despachosSeleccionados);

        for (const despachoId of despachosArray) {
          await estadoDespacho.actualizarDespacho(despachoId, 1, 1);
        }
      }

      // Los pagos múltiples se guardan en localStorage durante cada pago
      // Solo verificar que existan
      const pagosGuardados = localStorage.getItem("pagosMultiples");
      if (!pagosGuardados || JSON.parse(pagosGuardados).length === 0) {
        console.error("No se encontraron pagos múltiples guardados");
        setError("Error: No se encontraron los pagos realizados");
        setIsLoading(false);
        return;
      }

      console.log("Finalizando venta con pagos múltiples:", JSON.parse(pagosGuardados));

      // Limpiar pagos parciales ya que se completó la venta
      localStorage.removeItem("pagosParciales");

      navigate("/dialogoimprimirfacturapage");
    } catch (error) {
      console.error("Error al finalizar venta:", error);
      setError("Error al finalizar la venta");
      setIsLoading(false);
    }
  };

  const asignarMontoCompleto = () => {
    setMontoIngresado(montoRestante.toString());
  };

  const getNombreMetodo = metodo => {
    if (!metodo) return "";

    const nombres = {
      debito: "DÉBITO",
      credito: "CRÉDITO",
      efectivo: "EFECTIVO",
      qr: "QR FISERV",
    };
    return nombres[metodo] || metodo.toUpperCase();
  };

  // Función para manejar el click en volver
  const handleVolverClick = () => {
    if (pagosRealizados.length > 0) {
      setModalConfirmacionVolver(true);
    } else {
      // Limpiar pagos múltiples si no hay pagos realizados
      localStorage.removeItem("pagosMultiples");
      navigate("/formasdepagopage");
    }
  };

  // Función para volver guardando progreso automáticamente
  const volverConProgreso = () => {
    guardarProgresoPagos();
    // Limpiar pagos múltiples ya que se está saliendo sin completar
    localStorage.removeItem("pagosMultiples");
    navigate("/formasdepagopage");
  };

  return (
    <>
      <MenuSection />
      {isLoading && showLoadingSpinner()}

      <Box
        sx={{
          position: "absolute",
          top: window.innerWidth < 700 ? "8%" : "12%",
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          minHeight: "90vh",
        }}
      >
        {/* Header */}
        <h3
          style={{
            backgroundColor: theme.bandera.colorSecundario,
            color: "white",
            fontWeight: "bold",
            padding: "20px 22px",
            width: "100%",
            textAlign: "center",
            whiteSpace: "nowrap",
            margin: 0,
          }}
        >
          DIVIDIR PAGO
        </h3>

        {/* Información de montos */}
        <Box sx={{ textAlign: "center", mt: 3, mb: 2 }}>
          <Typography variant="h4" fontWeight="bold" color="black">
            ${montoTotal.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
          </Typography>
          <Typography variant="h6" color="gray" sx={{ mt: 1 }}>
            RESTAN: ${montoRestante.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
          </Typography>
        </Box>

        {/* Título */}
        <Typography variant="h6" color="black" sx={{ mb: 1, textAlign: "center" }}>
          Seleccione un método de pago
        </Typography>

        {/* Botones de métodos de pago */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${window.innerWidth < 700 ? 2:4}, 1fr)`,
            gap: "10px",
            alignItems: "center",
            mb: 4,
            px: 3,
          }}
        >
          <Button
            sx={{
              width: "120px",
              height: "120px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
            onClick={() => abrirModalMonto("debito")}
            disabled={montoRestante <= 0}
          >
            <img src={DebitoIcon} alt="Debito" style={{ width: "60px", marginBottom: "8px" }} />
            <Typography color="black" variant="subtitle2" fontWeight="bold">
              DÉBITO
            </Typography>
          </Button>

          <Button
            sx={{
              width: "120px",
              height: "120px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
            onClick={() => abrirModalMonto("credito")}
            disabled={montoRestante <= 0}
          >
            <img src={CreditoIcon} alt="Credito" style={{ width: "60px", marginBottom: "8px" }} />
            <Typography color="black" variant="subtitle2" fontWeight="bold">
              CRÉDITO
            </Typography>
          </Button>

          <Button
            sx={{
              width: "120px",
              height: "120px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
            onClick={() => abrirModalMonto("efectivo")}
            disabled={montoRestante <= 0}
          >
            <img src={EfectivoPagoIcon} alt="Efectivo" style={{ width: "50px", marginBottom: "8px" }} />
            <Typography color="black" variant="subtitle2" fontWeight="bold">
              EFECTIVO
            </Typography>
          </Button>

          <Button
            sx={{
              width: "120px",
              height: "120px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
            onClick={() => {
              setMetodoPagoSeleccionado("qr");
              setMontoIngresado("");
              setError("");
              setModalAbierto(true);
            }}
            disabled={montoRestante <= 0}
          >
            <img src={qr} alt="QR FISERV" style={{ width: "50px", marginBottom: "8px" }} />
            <Typography color="black" variant="subtitle2" fontWeight="bold">
              QR FISERV
            </Typography>
          </Button>
        </Box>

        {/* Botones de acción */}
        <Box
          sx={{
            position: "fixed",
            bottom: 40,
            left: 0,
            right: 0,
            display: "flex",
            padding: "10px",
            backgroundColor: "white",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            onClick={handleVolverClick}
            sx={{
              backgroundColor: theme.bandera.colorSecundario,
              mr: 2,
              color: "white",
              ":hover": {
                backgroundColor: theme.bandera.colorSecundario,
                opacity: 0.9,
              },
            }}
          >
            <Typography variant="button" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              VOLVER
            </Typography>
          </Button>

          {montoRestante <= 0 && (
            <Button
              variant="contained"
              onClick={finalizarVentaCompleta}
              sx={{
                backgroundColor: "#4caf50",
                ":hover": {
                  backgroundColor: "#45a049",
                },
              }}
            >
              <Typography variant="button" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                FINALIZAR
              </Typography>
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal para ingresar monto */}
      <Modal open={modalAbierto} onClose={cerrarModal} aria-labelledby="modal-monto">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "400px",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2, textAlign: "center" }}>
            Ingresar Monto - {metodoPagoSeleccionado ? getNombreMetodo(metodoPagoSeleccionado) : ""}
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, textAlign: "center", color: "gray" }}>
            Monto restante disponible:
          </Typography>

          <Typography variant="h5" sx={{ mb: 3, textAlign: "center", fontWeight: "bold" }}>
            ${montoRestante.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
          </Typography>

          <Typography variant="body1" sx={{ mb: 1 }}>
            Monto a pagar
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            value={montoIngresado}
            onChange={e => setMontoIngresado(e.target.value)}
            placeholder="0.00"
            type="number"
            inputProps={{
              step: "0.01",
              min: "0",
              max: montoRestante,
            }}
            sx={{ mb: 2 }}
          />

          <Button
            variant="outlined"
            fullWidth
            onClick={asignarMontoCompleto}
            sx={{ mb: 3, color: theme.bandera.colorSecundario, borderColor: theme.bandera.colorSecundario }}
          >
            TODO (${montoRestante.toLocaleString("es-AR", { minimumFractionDigits: 2 })})
          </Button>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex" }}>
            <Button variant="outlined" onClick={cerrarModal} disabled={procesandoPago} sx={{ flex: 1, mr: 2 }}>
              CANCELAR
            </Button>

            <Button
              variant="contained"
              onClick={procesarPago}
              disabled={procesandoPago || !montoIngresado}
              sx={{
                flex: 1,
                backgroundColor: theme.bandera.colorSecundario,
                ":hover": {
                  backgroundColor: theme.bandera.colorSecundario,
                  opacity: 0.9,
                },
              }}
            >
              PAGAR
            </Button>
          </Box>
        </Box>
      </Modal>
      {(procesandoPago || mensajeDeEspera) && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} sx={{ color: theme.bandera.colorSecundario }} />
          <Typography variant="h6" sx={{ mt: 3, fontWeight: "bold", textAlign: "center" }}>
            {mensajeDeEspera || "PROCESANDO PAGO..."}
          </Typography>
        </Box>
      )}

      {/* Modal de confirmación para volver */}
      <Dialog open={modalConfirmacionVolver} onClose={() => setModalConfirmacionVolver(false)} aria-labelledby="confirmacion-volver" maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold", color: "#f44336" }}>No es posible volver</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, textAlign: "center" }}>
            Ya realizaste pagos parciales por un total de:
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, textAlign: "center", fontWeight: "bold", color: theme.bandera.colorSecundario }}>
            ${pagosRealizados.reduce((sum, pago) => sum + pago.monto, 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: "center", color: "#f44336", fontWeight: "bold" }}>
            Debes completar el pago total antes de poder salir de esta pantalla.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, textAlign: "center", color: "gray" }}>
            Restan: ${montoRestante.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 3 }}>
          <Button
            variant="contained"
            onClick={() => setModalConfirmacionVolver(false)}
            sx={{
              backgroundColor: theme.bandera.colorSecundario,
              ":hover": { backgroundColor: theme.bandera.colorSecundario, opacity: 0.9 },
            }}
          >
            ENTENDIDO
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={showQRModal} onClose={() => {}} aria-labelledby="qr-modal">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 400,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Escaneá el código QR para pagar
          </Typography>

          <CircularProgress sx={{ mb: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img src={require("../images/qr-pago.png")} alt="QR" style={{ width: "200px", height: "200px" }} />
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Monto: ${montoIngresado}
          </Typography>

          <Button variant="contained" color="error" onClick={() => setShowQRModal(false)}>
            CANCELAR
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default DividirPagoPage;
